<template>
    <div id="app">
        <div class="app__content" v-if="initialized">
            <router-view></router-view>
        </div>

        <OtSplash v-else :spinner="$whitelabel.resources.spinner" />
    </div>
</template>

<script lang="ts">
import type { PreferredLocales } from '@openticket/vue-localization';
import type { TFAConfirmationData } from '@openticket/vue-tfa-confirmation';
import { CURRENCIES } from '@openticket/helpers';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Inject } from 'vue-property-decorator';
import { initGtm } from './services/util/gtm';
import {
    initRudderAnalytics,
    rudderAnalytics,
    rudderAnalyticsAnonymousIdFromHash,
    rudderAnalyticsPage,
} from './services/util/rudderstack';

@Component
export default class App extends Vue {

    public initialized = false;

    @Inject('otVueTFAConfirmationData')
    public tfaConfirmationData: TFAConfirmationData | undefined;

    public async created(): Promise<void> {
        try {
            await Promise.all([
                this.$whitelabel.init(),
                this.initLocalization(),
            ]);

            this.initRudderstackAnalytics();
            this.initGoogleTagManager();
        } catch (e) {
            console.error(e);

            await this.$router.push({ name: 'error' });
        }

        this.initialized = true;

        document.title = `${this.$whitelabel.name} - Login`;

        this.$whitelabel.setLocale(this.$localization.locale.locale);
    }

    private setTfaConfirmationTranslationData(): void {
        if (this.tfaConfirmationData) {
            this.tfaConfirmationData.setTranslationConfig({
                translatePath: (key: string, values?: { [key: string]: string }) => this.$t(key, values),
            });
        }
    }

    private async initLocalization(): Promise<void> {
        this.$localization.on('locale-change', (locale: string) => {
            try {
                this.$whitelabel.setLocale(locale);

                this.setTfaConfirmationTranslationData();
            } catch (e) { /* No-op */ }
        });

        const localeOverwrite: string | null = new URLSearchParams(window.location.search).get('locale') || null;
        const preferredLocales: PreferredLocales = this.$localization.preferredLocales();

        await this.$localization.init({
            locale: {
                defaults: [
                    preferredLocales.userOverwrite,
                    localeOverwrite,
                    preferredLocales.browser,
                ],
                supported: [ 'en_GB', 'en_US', 'nl_NL', 'de_DE', 'fr_FR', 'es_ES', 'tr_TR' ],
            },
            country: {
                // Custom overrides (note: global defaults should be in lib-localization)
                default_currencies: {
                    // Curacao (CW) uses XCG
                    CW: 'XCG',
                },
            },
            currency: {
                supported: [ ...CURRENCIES ] as [string, ...string[]],
            },
            urls: {
                translations_combination_key: 'login',
            },
        });

        this.setTfaConfirmationTranslationData();
    }

    private initGoogleTagManager(): void {
        if (!this.$whitelabel.login.gtm_code) {
            return;
        }

        initGtm(this.$whitelabel.login.gtm_code);
    }

    private initRudderstackAnalytics(): void {
        if (!this.$whitelabel.login.rudderstack_write_key || !this.$whitelabel.login.rudderstack_data_plane_url) {
            return;
        }

        initRudderAnalytics(
            this.$whitelabel.login.rudderstack_write_key,
            this.$whitelabel.login.rudderstack_data_plane_url,
            this.$whitelabel.login.rudderstack_config_url,
            this.$whitelabel.login.rudderstack_cdn_url,
        );

        const eaid = rudderAnalyticsAnonymousIdFromHash();
        if (eaid) {
            rudderAnalytics((rs) => rs.setAnonymousId(eaid));
            // Remove hash from url
            window.location.hash = '';
        }

        rudderAnalyticsPage('login');
    }

}
</script>

<style lang="scss" scoped>

</style>
